import { CONNECT_ROUTES_SET } from '@atlassian/jira-common-constants/src/spa-routes-sets.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { QUEUE_ROUTES } from '@atlassian/jira-servicedesk-common/src/navigation/queues/index.tsx';
import { WORK_CATEGORIES_QUEUE_SUPPORTED } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import type { RouterState as InternalRouterState } from '@atlassian/react-resource-router';
import { SERVICE_DESK_AUTOMATION_SUB_NAV } from '../../../common/constants.tsx';

type RouterState = Pick<InternalRouterState, 'action' | 'match' | 'route'>;

type Args = {
	connectStack: string[] | null;
	forgeStack: string[] | null;
	knowledgeNest: boolean;
	currentRouterState: RouterState;
	forceRootStack: boolean;
	previousState: RouterState | null;
	isLegacyAutomationEnabled: boolean;
	isTeamManagedProject: boolean;
	lastStackRef: React.MutableRefObject<string[]>;
};

const REPLACE = 'REPLACE';

const shouldForceRootStack = (state: RouterState | null) => {
	const { route, action } = state || {};

	// If we explicitly load on :projectKey/sections/:practiceType we want to load the nest
	const isLoadOnSectionRedirect =
		action !== REPLACE && route?.name === 'servicedesk-practice-landing';

	// When landing on these routes we don't want it to force reloading the stack regardless of isRedirect = true
	const isLandingPageRedirectRoute = [
		'project-settings-servicedesk-classic-apps', //  /settings/apps
		'project-settings-servicedesk-virtualagent', //  /settings/virtual-agent
	].includes(route?.name || '');

	return route?.isRedirect && !isLoadOnSectionRedirect && !isLandingPageRedirectRoute;
};

/**
 *
 * @param forceRootStack If true - `stack` is set to the root menu panel (`[]` value)
 */
export const calculateStack = ({
	connectStack,
	forgeStack,
	currentRouterState,
	forceRootStack,
	knowledgeNest,
	previousState,
	isLegacyAutomationEnabled,
	lastStackRef,
}: Args): string[] => {
	const { match, route } = currentRouterState;
	if (!route) return [];

	if (
		forceRootStack &&
		(shouldForceRootStack(previousState) || shouldForceRootStack(currentRouterState))
	) {
		return [];
	}

	if (
		[
			'servicedesk-practice-custom-and-issue',
			'servicedesk-practice-edit',
			'servicedesk-practice-issue',
			'servicedesk-practice-landing',
			'servicedesk-practice-new',
			'servicedesk-practice-manage-view',
			'servicedesk-queues-addon',
			'servicedesk-queues-custom-view',
			'servicedesk-queues-edit',
			'servicedesk-queues-issue',
			'servicedesk-queues-landing',
			'servicedesk-queues-new',
			'servicedesk-queues-manage-view',
			'servicedesk-queues-custom-issue-detail',
			...(fg('jsm_views_inside_queues_-_main_flag')
				? [
						'servicedesk-queues-custom-board',
						'servicedesk-queues-custom-calendar',
						'servicedesk-queues-practice-custom-board',
						'servicedesk-queues-practice-custom-calendar',
						'servicedesk-queues-views-config',
						'servicedesk-practice-views-config',
					]
				: []),
		]
			.filter(Boolean)
			.includes(route.name)
	) {
		const practice =
			WORK_CATEGORIES_QUEUE_SUPPORTED.find((v) => match.url.includes(`/${QUEUE_ROUTES[v]}`)) ||
			'queues';
		return [practice];
	}

	if (
		[
			'knowledge-hub-all-articles',
			'knowledge-hub-article-in-category',
			'knowledge-hub-browse-all-articles',
			'knowledge-hub-browse-view-article',
			'knowledge-hub-category',
			'knowledge-hub-category-page',
			'knowledge-hub-landing',
			'knowledge-hub-search',
			'knowledge-hub-setup',
			'knowledge-hub-drafts',
			'knowledge-hub-browse-all-categories',
			'knowledge-hub-suggested-topics',
		].includes(route.name) &&
		knowledgeNest
	) {
		return ['knowledge'];
	}

	if (
		[
			'servicedesk-reports',
			'servicedesk-atlassian-analytics',
			'servicedesk-reports-workload',
			'servicedesk-reports-satisfaction-time-scale-issue',
			'servicedesk-reports-satisfaction-time-scale',
			'servicedesk-reports-satisfaction',
			'servicedesk-kb-requests-reports-time-scale-issue',
			'servicedesk-kb-requests-reports-time-scale',
			'servicedesk-kb-requests-reports',
			'servicedesk-reports-custom-edit',
			'servicedesk-reports-custom-create',
			'servicedesk-reports-custom-time-scale-issue',
			'servicedesk-reports-custom-time-scale',
			'servicedesk-reports-custom',
			'servicedesk-reports-type',
		].includes(route.name)
	) {
		return ['reports'];
	}

	if (['servicedesk-issues'].includes(route.name)) {
		return ['issues'];
	}

	if (isLegacyAutomationEnabled) {
		if (
			[
				'legacy-jsd-jira-automation',
				'project-settings-automation',
				'project-settings-servicedesk-jira-automation',
			].includes(route.name) ||
			route.name.includes('project-settings-servicedesk-automation')
		) {
			return ['settings', SERVICE_DESK_AUTOMATION_SUB_NAV];
		}
	}

	// SERVICE_DESK_LEGACY_TICKET_TYPES - is a "Request types" menu panel
	const isOnSpaRequestTypePage =
		route.name === 'project-settings-servicedesk-itsm-ticket-types-config' ||
		route.name === 'project-settings-servicedesk-itsm-ticket-type-fields';

	if (
		route.name === 'project-settings-servicedesk-unassigned-ticket-types-config' ||
		route.name === 'project-settings-servicedesk-unassigned-ticket-type-fields'
	) {
		return ['settings'];
	}

	if (isOnSpaRequestTypePage) {
		return ['settings'];
	}

	if (route.name === 'project-settings-servicedesk-notifications-notification-email') {
		return ['settings'];
	}

	// should be handled above the generic PROJECT_SETTINGS_SERVICEDESK logic
	if (
		[
			'forge-spa-project',
			'forge-spa-project-servicedesk',
			'forge-spa-servicedesk-queue',
			'project-settings-servicedesk-forge-apps',
		].includes(route.name) &&
		forgeStack
	) {
		return forgeStack;
	}

	// generic JSM Project Settings / PROJECT_SETTINGS_SERVICEDESK logic
	if (
		route.name.includes('project-settings-servicedesk') ||
		route.name.includes('legacy-jsd-settings')
	) {
		const [view] = route.name
			.replace('project-settings-servicedesk-', '')
			.replace('legacy-jsd-settings-', '')
			.split('-');

		switch (view) {
			case 'channels':
			case 'issuetypes':
			case 'apps':
				return ['settings'];
			case 'notifications':
				return ['settings'];
			case 'language':
				if (match?.params?.languageKey != null) {
					return ['settings', 'languageSupport', match.params.languageKey];
				}
				return ['settings', 'languageSupport'];
			case 'virtualagent':
				return ['settings', 'virtualagent'];
			default:
				return ['settings'];
		}
	}

	if (
		[
			'legacy-project-settings-edit',
			'legacy-project-settings-config',
			'project-settings-classic-data-classifications',
			'legacy-project-settings-fields',
			'legacy-project-settings-issue-layout',
			fg('jcs_master_flag') && 'project-settings-classic-issue-layout',
			'legacy-project-settings-issue-security',
			'legacy-project-settings-issue-types',
			'legacy-project-settings-notifications',
			'legacy-project-settings-opsgenie',
			'legacy-project-settings-permissions',
			'legacy-project-settings-screens',
			'legacy-project-settings-summary',
			'legacy-project-settings-workflows',
			'legacy-project-settings-delete',
			'legacy-project-settings-collectors',
			'legacy-project-settings-dev',
			'legacy-project-settings-people',
			'project-settings-versions',
			'project-settings-components',
			'project-settings-automation',
			'legacy-jsd-settings-fallback',
			'legacy-jsd-widgets',
			'servicedesk-sla-settings',
			fg('playbooks-in-jsm') && 'project-settings-playbooks-edit',
			fg('playbooks-in-jsm') && 'project-settings-playbooks-list',
		].includes(route.name)
	) {
		return ['settings'];
	}

	if (CONNECT_ROUTES_SET.has(route.name) && connectStack) {
		return connectStack;
	}

	if (['issue-navigator'].includes(route.name)) {
		return ['issues'];
	}

	/*
        When navigating to the products or customers page, we don't need to
        retain lastStack to previous value, see the exception in ../index
        for the settings page. When navigating from the feature page settings
        to the products or customers page, the navbar stays on the settings.
        This sets the lastStack to an empty array to update the navbar.
    */
	if (['servicedesk-customer-service-products', 'servicedesk-customers-v2'].includes(route.name)) {
		// eslint-disable-next-line no-param-reassign
		lastStackRef.current = [];
		return [];
	}

	return [];
};
